<template>
    <div data-cy="cupones-excel-content">
        <!-- Imported terminal: highest priority so the success state
             is never shadowed by the file picker or the error table. -->
        <div
            v-if="phase === 'imported'"
            data-cy="cupones-excel-imported"
        >
            <div class="tw:rounded tw:border tw:border-green-300 tw:bg-green-50 tw:p-4">
                <p class="tw:text-sm tw:text-green-700 tw:font-semibold tw:mb-0">
                    Importación completada
                </p>
                <p
                    v-if="mensajeFinal"
                    class="tw:text-sm tw:text-green-700 tw:mt-1"
                >
                    {{ mensajeFinal }}
                </p>
            </div>
        </div>

        <!-- Validated with row errors: rendered BEFORE the file picker
             so the user sees the per-row error table even though
             `phase === 'failed'` is also true. The terminal state is
             the failure-with-errors table, not the picker. -->
        <div
            v-else-if="(phase === 'validated' || phase === 'failed') && filasConError > 0 && errores.length > 0"
            data-cy="cupones-excel-errors"
        >
            <p class="tw:text-sm tw:text-gray-700 tw:mb-3">
                Se han encontrado <strong>{{ errores.length }}</strong> errores en el archivo:
            </p>
            <BaseTable
                data-cy="cupones-excel-errors-table"
                :columns="erroresColumns"
                :data="erroresRows"
                row-key="row_number"
                :max-body-height="ERROR_TABLE_MAX_HEIGHT"
                default-col-width="auto"
                naked
                flat
            >
                <template #cell-row_number="{ value }">
                    <span class="tw:font-medium tw:text-gray-900">{{ value }}</span>
                </template>
                <template #cell-message="{ value }">
                    <span
                        class="tw:text-gray-700 tw:whitespace-normal tw:break-words tw:block"
                    >{{ value }}</span>
                </template>
            </BaseTable>
        </div>

        <!-- Already-validated (typically after a 409): the user
             already uploaded a workbook in this session. We show
             the summary + the two actions (Importar / Descartar y
             subir otro) and hide the file picker. -->
        <div
            v-else-if="phase === 'validated' && importId"
            data-cy="cupones-excel-existing"
        >
            <div
                v-if="cancelInFlight"
                class="tw:py-3"
                data-cy="cupones-excel-cancelling"
            >
                <p class="tw:text-sm tw:text-gray-700 tw:text-center">Descartando el Excel anterior…</p>
            </div>
            <div
                v-else
                class="tw:rounded tw:border tw:border-blue-200 tw:bg-blue-50 tw:p-3"
                data-cy="cupones-excel-existing-banner"
            >
                <p class="tw:text-sm tw:text-blue-800 tw:mb-0">
                    El excel se ha validado correctamente y está listo para importar.
                </p>
            </div>
        </div>

        <!-- Uploading: just informative text. We deliberately do NOT
             render a spinner: the SVG character's optical centre does
             not align with the text baseline under flex items-center,
             and ship-fast beats ship-perfect. The text alone is enough
             to communicate that the upload is in flight. -->
        <div
            v-else-if="phase === 'uploading'"
            data-cy="cupones-excel-uploading"
            class="tw:py-3"
        >
            <p class="tw:text-sm tw:text-gray-700 tw:text-center">
                Subiendo archivo… Esto puede tardar unos segundos.
            </p>
        </div>

        <!-- Validating / Importing with no progress data yet: just text. -->
        <div
            v-else-if="(phase === 'validating' || phase === 'importing') && totalFilas === 0"
            data-cy="cupones-excel-progress-pending"
            class="tw:py-3"
        >
            <p class="tw:text-sm tw:text-gray-700 tw:text-center">
                <span v-if="phase === 'validating'">Validando filas del archivo…</span>
                <span v-else>Importando cupones…</span>
            </p>
        </div>

        <!-- Validating / Importing with progress data: real progress bar. -->
        <div
            v-else-if="phase === 'validating' || phase === 'importing'"
            data-cy="cupones-excel-progress"
            class="tw:flex tw:flex-col tw:gap-3"
        >
            <p class="tw:text-base tw:text-gray-700">
                <span v-if="phase === 'validating'">Validando filas del archivo…</span>
                <span v-else>Importando cupones…</span>
            </p>
            <div
                role="status"
                aria-live="polite"
                data-cy="cupones-excel-progress-text"
            >
                <p class="tw:text-sm tw:text-gray-700">
                    <span data-cy="cupones-excel-progress-processed">{{ totalProcesadas }}</span>
                    /
                    <span data-cy="cupones-excel-progress-total">{{ totalFilas }}</span>
                    filas procesadas
                </p>
                <div class="tw:w-full tw:bg-gray-200 tw:rounded-full tw:h-2 tw:mt-2">
                    <div
                        class="tw:bg-blue-500 tw:h-2 tw:rounded-full tw:transition-all tw:duration-200"
                        :style="{ width: progress + '%' }"
                    ></div>
                </div>
                <p class="tw:text-xs tw:text-gray-500 tw:mt-1">
                    {{ progress }}%
                </p>
            </div>
        </div>

        <!-- Failed terminal (no per-row errors): only the error banner.
             The file picker is intentionally hidden here so the user
             cannot pick a new file until they explicitly hit Reintentar
             (which dispatches the DELETE and resets the composable). -->
        <div
            v-else-if="phase === 'failed' && apiError"
            class="tw:rounded tw:border tw:border-red-300 tw:bg-red-50 tw:p-3"
            data-cy="cupones-excel-failed"
        >
            <p class="tw:text-sm tw:text-red-700">
                <strong>Error:</strong> {{ apiError }}
            </p>
        </div>

        <!-- File picker (idle only). The picker is hidden during any
             active phase AND during the failed terminal state so the
             user must click Reintentar to recover. -->
        <div v-else>
            <FileInput
                ref="fileInputRef"
                label="Archivo Excel"
                hint="(.xlsx, máx. 5 MB)"
                accept=".xlsx"
                data-cy="cupones-excel-file"
                error-data-cy="cupones-excel-file-error"
                :error="fileErrorMessage"
                :disabled="phase === 'uploading'"
                @file-selected="onFileChange"
            />
            <p
                class="tw:text-xs tw:text-gray-600 tw:mt-2 tw:mb-0"
                data-cy="cupones-excel-format-hint"
            >
                El archivo debe tener una primera fila con las columnas:
                <strong>Nombre</strong>, <strong>Código</strong>,
                <strong>Descuento en €</strong>, <strong>Descuento en %</strong>,
                <strong>Unidades</strong>. El nombre de cada columna puede ir
                en minúsculas o sin acentos, pero debe contener exactamente
                esos textos.
            </p>
        </div>

        <!-- Action footer -->
        <div class="tw:flex tw:justify-end tw:gap-2 tw:pt-4">
            <Button
                v-if="phase === 'validated' && filasConError === 0 && importId && !cancelInFlight"
                data-cy="cupones-excel-import-button"
                variant="primary"
                @click="onImportClick"
            >
                Importar {{ totalFilas > 1 ? 'cupones' : 'cupón' }}
            </Button>

            <Button
                v-if="phase === 'validated' && importId && !cancelInFlight"
                data-cy="cupones-excel-discard-button"
                variant="border"
                @click="onCancelClick"
            >
                Descartar y subir otro
            </Button>

            <Button
                v-if="phase === 'failed' && errores.length === 0"
                data-cy="cupones-excel-retry-button"
                @click="onRetryClick"
            >
                Reintentar
            </Button>

            <Button
                v-if="((phase === 'validated' || phase === 'failed') && filasConError > 0 && errores.length > 0)"
                data-cy="cupones-excel-errores-cerrar-button"
                variant="border"
                @click="emitCerrar({ imported: false })"
            >
                Cerrar
            </Button>
        </div>
    </div>
</template>

<script setup>
    import { computed, ref, watch } from 'vue'
    import { useCouponExcelImport } from '@/composables/admin/abonos/precios/vouchers/useCouponExcelImport'
    import Button from '@/modules/admin/ui/Button.vue'
    import BaseTable from '@/modules/admin/ui/table/BaseTable.vue'
    import FileInput from '@/modules/admin/ui/FileInput.vue'

    const ERROR_TABLE_MAX_HEIGHT = '260px'

    const erroresColumns = [
        { key: 'row_number', label: 'Fila', field: 'row_number', class: 'tw:w-16', centered: true },
        { key: 'column', label: 'Columna', field: 'column', class: 'tw:w-36' },
        { key: 'message', label: 'Detalle', field: 'message' }
    ]

    const emit = defineEmits(['cerrar'])

    const {
        phase,
        importId,
        progress,
        totalFilas,
        totalProcesadas,
        filasConError,
        errores,
        mensajeFinal,
        apiError,
        uploadFileError,
        cancelInFlight,
        uploadFile,
        triggerImport,
        cancelImport,
        retry
    } = useCouponExcelImport()

    const fileInputRef = ref(null)

    const fileErrorMessage = computed(() => {
        if (uploadFileError.value === 'xlsx_only') return 'Solo se permiten archivos .xlsx'
        if (uploadFileError.value === 'size_exceeded') return 'El archivo supera el tamaño máximo de 5 MB'
        return ''
    })

    const erroresRows = computed(() => errores.value || [])

    function onFileChange(file) {
        if (!file) return
        uploadFile(file)
    }

    function onImportClick() {
        triggerImport()
    }

    function onRetryClick() {
        // Always try to cancel the previous import on the server first
        // (if there is one in flight or validated). cancelImport is a
        // no-op when there is no `importId`, so this is safe in every
        // branch. After the cancel resolves the composable resets phase
        // to `idle`; if the cancel was a no-op we still call retry()
        // so the modal returns to a clean file picker.
        cancelImport().finally(() => {
            if (phase.value !== 'idle') {
                retry()
            }
            if (fileInputRef.value) fileInputRef.value.reset()
        })
    }

    function onCancelClick() {
        cancelImport()
    }

    function emitCerrar({ imported } = {}) {
        emit('cerrar', { imported: Boolean(imported) })
    }

    // When phase becomes 'imported', close the modal exactly once per
    // importId with `imported: true` so the parent can refresh the voucher
    // list. We do NOT render a separate "Cerrar" button in this terminal
    // state — the import has succeeded, so the modal closes on its own.
    const emittedFor = ref(null)
    watch(phase, newPhase => {
        if (newPhase === 'imported' && emittedFor.value !== importId.value) {
            emittedFor.value = importId.value
            emitCerrar({ imported: true })
        }
    })

    /**
     * Public API for the parent modal. Resets the composable so the next
     * time the user opens the Excel tab, no leftover state remains from
     * the previous session.
     */
    function reset() {
        retry()
        if (fileInputRef.value) fileInputRef.value.reset()
    }

    defineExpose({ reset })
</script>
